import * as React from 'react';
import Button from '@mui/material/Button';
import {
    Alert,
    Box,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    Typography,
    Chip,
} from '@mui/material';
import { getPromoBanners, getShopLayoutSettings, getShopProducts, updateShopLayoutSettings } from '../Api';
import { useNavigate } from 'react-router-dom';
import ShopItemCard from '../components/ShopItemCard';
import ShopItemDto from '../models/dtos/ShopItemDto';

export const formatRevenue = (revenue: number) => {
    return revenue.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
    });
}

export default function ShopPage() {
    const [state, setState] = React.useState({
        itemsLoaded: false,
        osTypes: ['All', 'iOS', 'Android'],
        osType: 'All',
        items: [] as ShopItemDto[],
    });
    const [statusFilter, setStatusFilter] = React.useState<'active' | 'disabled' | 'all'>('active');
    const [settingsLoading, setSettingsLoading] = React.useState(true);
    const [settingsSaving, setSettingsSaving] = React.useState(false);
    const [shopV2Enabled, setShopV2Enabled] = React.useState(false);
    const [settingsMessage, setSettingsMessage] = React.useState<string | null>(null);

    const navigate = useNavigate();

    const fetchData = async (os: string) => {
        const osType = os.toLowerCase() == 'all' ? undefined : os.toLowerCase();
        let items = await getShopProducts(osType);
        setState(prevState => ({ ...prevState, items: items ?? [], itemsLoaded: true }));
    }

    const onOsTypeChanged = (osType: string) =>{
        setState(prevState => ({...prevState, osType: osType, itemsLoaded: false}));
        fetchData(osType);
    }

    React.useEffect(() => {
        fetchData(state.osType);
    }, []);

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            setSettingsLoading(true);
            const dto = await getShopLayoutSettings();
            if (!cancelled) {
                setShopV2Enabled(dto?.shopV2LayoutEnabled ?? false);
                setSettingsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const onSaveLayoutSettings = async () => {
        setSettingsSaving(true);
        setSettingsMessage(null);
        const ok = await updateShopLayoutSettings({ shopV2LayoutEnabled: shopV2Enabled });
        setSettingsSaving(false);
        if (!ok) {
            setSettingsMessage('Failed to save shop layout settings. Check server logs and API connection.');
            return;
        }
        const dto = await getShopLayoutSettings();
        setShopV2Enabled(dto?.shopV2LayoutEnabled ?? shopV2Enabled);
        setSettingsMessage('Saved to qbet_app_settings (shop_v2_layout_enabled). Android picks this up on the next shop load.');
    };

    const activeCount = state.items.filter((i) => !i.isDeleted).length;
    const disabledCount = state.items.filter((i) => i.isDeleted).length;
    const filteredItems = state.items.filter((item) => {
        if (statusFilter === 'active') return !item.isDeleted;
        if (statusFilter === 'disabled') return item.isDeleted;
        return true;
    });

    return (
        <Stack gap="50px 50px" sx={{ mx: 5, my: 5 }} justifyContent='center' alignItems='center'>
            <Grid container spacing={2} columns={{ xs: 1, sm: 2, md: 4, lg: 5 }} justifyContent="flex-end" width="100%">
                <Grid item xs={1} key="addButton" alignContent="center">
                    <Button variant="contained" fullWidth={true} style={{ height: '54px' }} onClick={() => navigate('/shop-items/create')}>Add shop item</Button>
                </Grid>
            </Grid>

            <Card sx={{ width: '100%', maxWidth: 720 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>Shop layout (Android)</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Controls Android UI only. When enabled: sectioned shop + <code>new_shop_*</code> catalog (HTML).
                        When disabled: legacy grid + legacy SKUs (excludes <code>new_shop_*</code>).
                        Key: shop_v2_layout_enabled.
                    </Typography>
                    {settingsLoading ? (
                        <CircularProgress size={24} />
                    ) : (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={shopV2Enabled}
                                        onChange={(e) => setShopV2Enabled(e.target.checked)}
                                        color="warning"
                                    />
                                }
                                label={shopV2Enabled ? 'Shop v2 enabled' : 'Shop v2 disabled (legacy grid)'}
                            />
                            <Button
                                variant="contained"
                                onClick={onSaveLayoutSettings}
                                disabled={settingsSaving}
                            >
                                {settingsSaving ? 'Saving…' : 'Save layout setting'}
                            </Button>
                        </Stack>
                    )}
                    {settingsMessage ? (
                        <Box sx={{ mt: 2 }}>
                            <Alert severity={settingsMessage.startsWith('Failed') ? 'error' : 'success'}>
                                {settingsMessage}
                            </Alert>
                        </Box>
                    ) : null}
                </CardContent>
            </Card>

            <Typography variant="h3">Shop Items</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} width="100%">
                <Chip label={`${activeCount} active`} color="success" variant="outlined" size="small" />
                <Chip label={`${disabledCount} disabled`} color={disabledCount > 0 ? 'warning' : 'default'} variant="outlined" size="small" />
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    Disabled items are hidden in the app after API deploy; they stay visible here for audit.
                </Typography>
            </Stack>
            <Grid container spacing={5} columns={{ xs: 1, sm: 1, md: 2, lg: 3, xl: 4 }} width="100%">
                <Grid item sm={5} key="osselect">
                    <FormControl fullWidth>
                        <InputLabel id="demo-location-select-label">Operating System</InputLabel>
                        <Select
                            labelId="banner-simple-select-label"
                            id="location-simple-select"
                            value={state.osType}
                            onChange={e => onOsTypeChanged(e.target.value)}
                            label="Type">
                            {
                                state.osTypes.map(x => <MenuItem value={x} key={x}>{x}</MenuItem>)
                            }
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item sm={5} key="statusselect">
                    <FormControl fullWidth>
                        <InputLabel id="shop-status-filter-label">Status</InputLabel>
                        <Select
                            labelId="shop-status-filter-label"
                            id="shop-status-filter"
                            value={statusFilter}
                            onChange={e => setStatusFilter(e.target.value as 'active' | 'disabled' | 'all')}
                            label="Status">
                            <MenuItem value="active">Active only</MenuItem>
                            <MenuItem value="disabled">Disabled only</MenuItem>
                            <MenuItem value="all">All items</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
            {!state.itemsLoaded ? <Grid item xs={2}><CircularProgress size='25%' style={{ marginBottom: '32px', marginLeft: '35%' }} /></Grid> : <div />}
            <Grid container spacing={5} columns={{ xs: 1, sm: 1, md: 2, lg: 3, xl: 4 }} width="100%">
                <Box width='100%' />
                {filteredItems.length === 0 && state.itemsLoaded ? (
                    <Grid item xs={12}>
                        <Alert severity="info">
                            No shop items match the current filters.
                            {statusFilter === 'active' && disabledCount > 0
                                ? ` ${disabledCount} disabled item(s) — switch Status to “Disabled only” or “All items”.`
                                : ''}
                        </Alert>
                    </Grid>
                ) : null}
                {
                    filteredItems.map(x =>
                        <Grid item xs={1} key={x.id}>
                            <ShopItemCard {...{ item: x, showEdit: true, previewLanguage: 'en', onStatusChanged: () => fetchData(state.osType) }} />
                        </Grid>)
                }
            </Grid>
        </Stack >
    );
}
